import type { Me } from '@/lib/api';

/**
 * What the one button on the sign-in landing page is about to do (SYS-8,
 * SYS-10).
 *
 * **A module of its own, holding two functions, after four rewrites of the page
 * that used to hold them.** Every one of those rewrites moved the same decision
 * around inside a component — tangled with `useState`, with an effect ordering,
 * and with whichever `LandingState` shape was current that week — and the thing
 * that kept going wrong was never the rendering. So the decision is a pure
 * function of six facts, in a file that imports no React, touches no storage,
 * opens no network, and can be read in one screenful and tested as a table.
 *
 * `react-refresh/only-export-components` asked for the split too, and it was
 * right for its own smaller reason.
 *
 * **The second function is `showsPasscodeLink`, and it belongs here for the
 * reason the first one does** (ADR-0083). It is the only place on this page where
 * a second control's visibility is decided, it reads the same inputs, and its one
 * hard rule — never beside a restricted session — is the sort of thing that gets
 * lost the moment it is written as a JSX conditional.
 */

export type Offer =
  /**
   * Ask for an address, here, and then for the code (SYS-7).
   *
   * **This used to mean "leave for Entra".** The value and every branch that
   * reaches it are unchanged; what changed is that the passcode is now three
   * fields on this page rather than a redirect to a Microsoft one.
   */
  | 'email'
  /** Open an authenticator (SYS-8). */
  | 'passkey'
  /** Create a credential on this device (USR-11), in the own-device shape. */
  | 'enrol'
  /**
   * Hand over to the app. Reachable only for a session that is not restricted.
   *
   * **Taken rather than offered** (ADR-0104). This is the one answer the page
   * acts on itself: it is what "there is nothing left to ask you" is called, and
   * a button naming it stood between every reload and the address it started
   * at. The label survives for the frame before the hand-over and for a
   * capability probe that never settles — `SignInLanding.tsx` has both.
   */
  | 'continue'
  /**
   * This build can sign nobody in, and says so rather than pretending.
   *
   * **A two-origin build with no other arrangement for the session.** The SPA
   * and the API on different registrable domains means no cookie can exist —
   * which is already why the passkey control is withheld (`canHoldSession`).
   * The passcode ends the same way: the API would mint a cookie the browser
   * discards, and the person would be returned to this page having done
   * everything right.
   *
   * **No environment that exists is in this state today** (ADR-0059). It was
   * deployed dev until dev began carrying the session handle itself, and this
   * stays because it is still the correct answer for a two-origin build without
   * that flag — which is what any new environment starts as, and what dev would
   * revert to if `VITE_SIMULATED_AUTH` were dropped from its row.
   *
   * **Loud rather than silent**, which is the property `canHoldSession`'s own
   * header argues for. The alternative — a button that runs a whole ceremony
   * and lands back where it started — is the failure mode this codebase has
   * paid for twice.
   */
  | 'unavailable'
  /**
   * A credential is the way forward and no admin has authorised one (USR-14).
   *
   * **The same shape as `'unavailable'` and for the same reason**: the page has
   * finished checking, the answer is that this person cannot proceed from here,
   * and a disabled control naming the remedy beats a live one that runs a
   * ceremony and comes back with a 403. `requireEnrollmentUnlocked` would refuse
   * `register/options` before an authenticator ever prompted, so the button
   * would fail without even producing the gesture that made it feel like a try.
   *
   * **It is reached only where enrolling was the answer**, which in practice
   * means a **restricted** session (SYS-10) whose only exit is a new credential.
   * That is the case worth building for: without this the one control on the
   * page is a dead end rather than a refusal. An unrestricted session with no
   * passkey is simply let through instead — see `offerFor`'s last branch.
   *
   * The two differ in what they say. `'unavailable'` is about the build;
   * this is about the account, and the remedy is a person rather than a
   * deployment.
   */
  | 'locked';

/**
 * Which act did not complete, as an *input* to the decision rather than a state
 * around it.
 *
 * A failed assertion has to change what the button does next, and what it does
 * next is this function's answer. Making it a phase of the page instead was the
 * shape that produced a version where a restricted session's failed assertion
 * offered it the passcode it had just come back from.
 */
export type Failure = 'assert' | 'enrol';

export interface OfferInput {
  /** `GET /me`, or `undefined` for a visitor with no session. */
  readonly me: Me | undefined;
  /**
   * Somebody took the "Use an email code instead" link (ADR-0083).
   *
   * **An input rather than a phase of the page**, on `failure`'s reasoning
   * immediately below: it changes what the one control does next, and what it
   * does next is this function's answer. It is also the only input here that is a
   * *choice* rather than an observation, which is exactly why it must be read on
   * one branch and ignored on the others — see `offerFor`'s restricted case.
   */
  readonly passcodeRequested: boolean;
  /** `undefined` until the capability probe answers. Never reads as `true`. */
  readonly platformAvailable: boolean | undefined;
  readonly enrolmentDeferred: boolean;
  readonly failure: Failure | undefined;
  /** This browser can perform a ceremony *and* this build can complete one. */
  readonly canAssert: boolean;
  /**
   * This build can hold the session a passcode would produce.
   *
   * The same `canHoldSession` that gates `canAssert`, asked about the other
   * credential — and it is a separate input rather than one flag because the
   * two questions have different other halves. `canAssert` also asks whether
   * the *browser* has WebAuthn; nothing about the passcode depends on the
   * browser at all.
   */
  readonly canPasscode: boolean;
}

/**
 * The whole decision.
 *
 * Read the branches in order — each is a question that makes the next simpler,
 * and the ordering is the argument:
 *
 * 1. **No session**, so `/me` can say nothing — and nothing else can either, so
 *    this branch stopped guessing (ADR-0083). It offers the passkey to anybody
 *    whose browser and build can complete one, and `showsPasscodeLink` puts the
 *    other credential a click below it.
 * 2. **Restricted**, checked before anything about capability or preference,
 *    because this branch is the one that must not reach `'continue'`. Its two
 *    acts are `enrol` then `passkey`, in that order, and the transition between
 *    them is a credential appearing in `passkeyCount` — not a wizard step.
 * 3. **A full session with no credential anywhere**, which is the nudge that
 *    used to be a whole routeless screen. Gated on a platform authenticator
 *    because the landing page enrols in the own-device shape, and on `deferred`
 *    because "Not now" has to mean it.
 * 4. **Otherwise the door.**
 *
 * **`passkeyCount`, and never a fact about the browser, on every signed-in
 * branch.** `/me` describes the account, and it is what the server will actually
 * check. It also means the nudge does not fire for somebody who enrolled on their
 * phone last week, which is the complaint the old interstitial collected.
 *
 * _Amended (ADR-0083)._ There used to be a seventh input — `passkeyUsedHere`, a
 * `localStorage` flag written after any ceremony this browser completed — and
 * branch 1 offered the passkey only when it was set. It was a guess about a fact
 * WebAuthn deliberately refuses to expose, and iOS clears the storage it lived in
 * on a cadence of its own, so the people it existed to serve were returned to the
 * passcode roughly daily. Nothing replaced it: the guess was the bug.
 */
export function offerFor(input: OfferInput): Offer {
  const {
    me,
    passcodeRequested,
    platformAvailable,
    enrolmentDeferred,
    failure,
    canAssert,
    canPasscode,
  } = input;

  if (me === undefined) {
    /*
      **The passkey is the default, and the three exits from it are all facts
      rather than guesses** (ADR-0083): the browser or the build cannot complete
      an assertion, one was just attempted and failed, or the person said they
      wanted the other credential. What is *not* here any more is a prediction
      about whether a credential exists — see the amendment above.

      The cost of the reversal is one dismissed system sheet for somebody who has
      no passkey and ignores the link, and `failure === 'assert'` is what makes
      that cost bounded: the second render of this branch is the passcode form.
    */
    if (!canAssert || failure === 'assert' || passcodeRequested) {
      // **`'unavailable'` only reaches a visitor**, and that is not an
      // omission. Every branch below has a `me`, which means a session already
      // exists — and a build that cannot hold one could not have produced it.
      // The combination is unreachable rather than handled.
      return canPasscode ? 'email' : 'unavailable';
    }

    return 'passkey';
  }

  if (me.restricted) {
    /*
      **No `canAssert` here, and the omission is deliberate.** A build that
      cannot hold a session cannot mint a restricted one either — the exchange
      declines cross-origin (`canHoldSession`), so there is no session row for
      SYS-10 to restrict. The combination is unreachable rather than handled, and
      the page's `assert` degrades it to `'enrol'` if it ever is reached, which
      terminates. What must never happen on this branch is `'continue'`.
    */
    const enrolling = failure === 'assert' || me.passkeyCount === 0;

    /*
      **USR-14, and this branch is the reason `/me` carries the flag at all.** A
      restricted session's only exit is a new credential (SYS-10), so a locked
      account here has no way forward from this page — and the honest rendering
      of "no way forward" is a control that says so, not one that opens an
      authenticator and then gets a 403 from `register/options`.

      Checked *inside* the enrolling case rather than above it: a restricted
      session that still holds a credential is offered `'passkey'`, and
      asserting is not gated by the lock. Somebody in that state gets out
      without troubling anybody, which is the common path and must stay open.
    */
    if (enrolling && !me.enrollmentUnlocked) return 'locked';

    return enrolling ? 'enrol' : 'passkey';
  }

  /*
    **A failed enrolment does not fall through to `'continue'`, and that was a
    real bug for exactly one commit.** Falling through left the button labelled
    "Could not add a passkey — try again" while a click on it entered the app — a
    control whose label promised a retry and delivered a door. The offer stays
    put and "Not now" is the way past, which is the same one click and says what
    it does.
  */
  /*
    **A locked account skips the nudge rather than being stopped by it**
    (USR-14). This branch is an invitation, not a requirement — an unrestricted
    session has already proved itself and is entitled to the app. Offering
    `'enrol'` here to somebody the API would refuse would turn an optional
    prompt into a wall, and `'locked'` would be worse still: a dead end on a
    path that had a perfectly good way past it.
  */
  if (
    canAssert &&
    me.passkeyCount === 0 &&
    me.enrollmentUnlocked &&
    platformAvailable === true &&
    !enrolmentDeferred
  ) {
    return 'enrol';
  }

  return 'continue';
}

/**
 * Whether "Use an email code instead" sits under the button (ADR-0083).
 *
 * **The whole of what a browser that has forgotten everything looks different
 * by.** Since `offerFor` stopped guessing, a returning member and a first-time
 * one see the same primary control; this link is how the second one gets on.
 *
 * Three conditions, and the third is the one worth reading twice:
 *
 * 1. **The offer is `'passkey'`.** Beside the passcode form it would be a second
 *    route to the control already on screen; beside `'continue'`, `'enrol'`,
 *    `'locked'`, or `'unavailable'` it would be a non-sequitur. ADR-0051's
 *    one-primary-control rule survives a second element only while that element
 *    is secondary *and* points somewhere the primary does not.
 * 2. **`canPasscode`.** The link names an act, and a build that cannot complete
 *    one must not offer it — the same argument `'unavailable'` is made of.
 * 3. **No session at all.** `'passkey'` is *also* what a restricted session is
 *    offered (SYS-10), and that session is restricted precisely *because* it
 *    used a passcode while holding a credential. Offering it the code is the
 *    loop ADR-0051 removed, and it would be a loop with a ratchet: each trip
 *    round mints another restricted session. This is why the rule cannot be read
 *    off the offer alone, and why it is a function rather than a JSX conditional.
 */
export function showsPasscodeLink(input: OfferInput): boolean {
  return input.me === undefined && input.canPasscode && offerFor(input) === 'passkey';
}
